import { api } from '@/lib/api';
import { IAM_BASE } from '@/api/paths';
import type { ApiEnvelope } from '@/api/iam';

/**
 * BANKA HESAPLARI UCU.
 *
 * TEK UC IKI ISI YAPAR: `POST /v1/accounts` hem olusturur hem gunceller —
 * govdede `id` varsa guncelleme. Ayri bir PUT/PATCH YOK.
 *
 * SILME UCU DE YOK. Silmek `accountIsActive: false` yazmaktir (yumusak silme);
 * satir veritabaninda kalir, gecmis islemlerdeki referanslar kirilmaz.
 *
 * KAPSAM ISTEMCIDEN GONDERILMEZ. Sunucu cagirani jetondan cozuyor
 * (account-service.js): musteri kendi hesaplarini, buro uyesi BURONUN
 * hesaplarini gorur. `accountUserId` gondermek en iyi ihtimalle etkisiz, en
 * kotu ihtimalle "baskasinin IBAN'ini iste" anlamina gelen bir aliskanlik
 * uretir — sunucu okumuyor, biz de gondermiyoruz.
 */

/** Sunucudan gelen ham satir. pg int8'i STRING dondurur; kimlikler sayiya CEVRILMEZ. */
export type RawAccount = {
  id: string | number;
  accountCode?: string | null;
  accountLabel?: string | null;
  accountName?: string | null;
  accountCurrency?: string | null;
  accountIbanCode?: string | null;
  userId?: string | number | null;
  organizationId?: string | number | null;
  isActive?: boolean;
};

type AccountListResponse = ApiEnvelope & { accountList?: RawAccount[] };

/** Ekranin calistigi normallestirilmis kayit. Her kimlik `string`. */
export type BankAccount = Readonly<{
  id: string;
  /** Banka kodu (`accountCode`). Katalogda yoksa da tasinir; gruplama buna gore. */
  bankCode: string;
  /** Sunucudaki `accountLabel` — katalogda kod bulunamazsa gorunen ad olur. */
  bankLabel: string;
  accountName: string;
  currency: string;
  /** TAM IBAN. Listede maskeli gosterilir (bkz. src/lib/iban-format.ts). */
  iban: string;
}>;

const toAccount = (raw: RawAccount): BankAccount => ({
  id: String(raw.id),
  bankCode: raw.accountCode ?? '',
  bankLabel: raw.accountLabel ?? '',
  accountName: raw.accountName ?? '',
  currency: raw.accountCurrency ?? '',
  iban: raw.accountIbanCode ?? '',
});

/**
 * Hesaplari listeler.
 *
 * Parametre ALINMAZ: filtrelenecek tek eksen kapsam ve o sunucuda. IBAN'i
 * sorgu dizesine sokan bir parametre de bilincli olarak yok — sorgu dizeleri
 * eriaim kayitlarina, proxy loglarina ve tarayici gecmisine yazilir.
 */
export const fetchAccounts = async (): Promise<BankAccount[]> => {
  const { data } = await api.get<AccountListResponse>(`${IAM_BASE}/v1/accounts`);
  // HTTP 200 + code:'error' mumkun; yalniz duruma bakan bir cagri bos listeyi
  // "hesabiniz yok" diye gosterirdi.
  if (data.code !== 'success') throw new Error(data.message ?? 'ACCOUNT_LIST_FAILED');
  return (data.accountList ?? []).map(toAccount);
};

export type SaveAccountInput = {
  /** Varsa GUNCELLEME, yoksa OLUSTURMA. Uc bunu govdedeki id'ye gore ayirir. */
  id?: string;
  bankCode: string;
  bankLabel: string;
  accountName: string;
  currency: string;
  /** Normallestirilmis (bosluksuz, buyuk harfli) IBAN. */
  iban: string;
};

export const saveAccount = async (input: SaveAccountInput): Promise<void> => {
  const { data } = await api.post<ApiEnvelope>(`${IAM_BASE}/v1/accounts`, {
    // `exactOptionalPropertyTypes` altinda `id: undefined` yazmak tip hatasi;
    // ayrica govdede duran bir `id: undefined` alani sunucuda "guncelleme"
    // dallanmasini tetiklemez ama okuyani yaniltir.
    ...(input.id === undefined ? {} : { id: input.id }),
    accountCode: input.bankCode,
    accountLabel: input.bankLabel,
    accountName: input.accountName,
    accountCurrency: input.currency,
    accountIbanCode: input.iban,
    // Sunucu bu alani JSONB kolonuna yaziyor ve verilmezse guncellemede
    // dokunmuyor; mobil ile ayni sekilde bos nesne gonderiliyor.
    accountData: {},
  });
  if (data.code !== 'success') throw new Error(data.message ?? 'ACCOUNT_SAVE_FAILED');
};

/**
 * YUMUSAK SILME. `DELETE` ucu yok; satir `is_active=false` ile listeden duser.
 * Baska hicbir alan gonderilmez — gonderilseydi "sil" islemi ayni zamanda
 * sessiz bir guncelleme olurdu.
 */
export const deactivateAccount = async (id: string): Promise<void> => {
  const { data } = await api.post<ApiEnvelope>(`${IAM_BASE}/v1/accounts`, {
    id,
    accountIsActive: false,
  });
  if (data.code !== 'success') throw new Error(data.message ?? 'ACCOUNT_DELETE_FAILED');
};
